/**
 * Send All chunking orchestrator.
 *
 * The cloud POST /api/intake/send processes its candidate set in one serial pass inside one HTTP
 * request. A single "Send All" over the whole sendable list (the 250-record backlog, 2026-06-18)
 * does enough SharePoint + Graph work per record to blow past the platform's request cap -> the
 * platform kills the request -> the UI sees a 500. NOTE (2026-07-09): that cap is the Static Web Apps
 * managed-functions limit of ~45s, NOT the ~230s this comment used to claim; the 60-record press that
 * day died at ~45s per chunk. The fix is to never send the whole
 * set in one call: chunk the ids and POST each chunk sequentially, each with its OWN clientRequestId
 * so the server's idempotency cache (keyed by jti+clientRequestId) does not collapse the chunks into
 * one replayed result. Each chunk is small, so no call approaches the timeout; the server stays the
 * authoritative gate (it re-reads each sidecar under lock and skips anything already sent), so a
 * stale client id list never over-sends and a partial run is resumable by pressing Send All again.
 *
 * Pure + dependency-injected (the POST fn and the base id are passed in) so it unit-tests with no
 * network, no React, and no crypto.
 */

/**
 * Records per chunk. Small enough that one /send call finishes well inside the Static Web Apps
 * managed-functions request cap (~45s). Lowered 25 -> 10 on 2026-07-09: a 25-record chunk of genuinely-new
 * records ran ~45s and was killed mid-flight, surfacing as a bare 500 after it had already committed most
 * of the chunk. The server's own INTAKE_SEND_DEADLINE_MS guard is now 30s (under the cap), so an
 * over-budget chunk returns a clean `hasMore` instead of dying.
 */
export const SEND_ALL_CHUNK_SIZE = 10;

/** The subset of the /send response runSendAll reads, plus the apiPost { ok, status } envelope. */
export interface SendChunkResponse {
  ok: boolean;
  status: number;
  data: {
    counts?: { sent?: number; skipped?: number };
    emailedNoConsent?: number;
    emailedUnableToTest?: number;
    error?: string;
    /**
     * The server tripped its own wall-clock deadline before finishing this chunk: its tail records were
     * deferred (NOT sent - they fold into counts.skipped with reason 'deadline') and need a re-press. A
     * 10-record chunk should essentially never hit the 30s budget, but if it does this is the ONLY signal
     * that distinguishes a deferred record from a benign already-sent / not-found skip, so it must surface.
     */
    hasMore?: boolean;
  };
}

/** What one Send All press accomplished across all its chunks. */
export interface SendAllOutcome {
  /** Number of chunks attempted. */
  chunks: number;
  /** Records the server reported sent across all chunks. */
  sent: number;
  /** Records the server reported skipped (already sent, not found, bad id, deadline) across all chunks. */
  skipped: number;
  /** Member Support emails (no-consent + unable-to-test notices) across all chunks - same recipient. */
  emailed: number;
  /** Per-chunk failure messages (a non-2xx or a thrown error). A failed chunk does NOT abort the rest. */
  errors: string[];
  /**
   * True when any chunk reported hasMore (the server deferred part of it on its deadline). Those records
   * are unsent but still sendable, so the caller must prompt a re-press rather than report a clean finish.
   */
  hasMore: boolean;
}

/** Split `items` into consecutive runs of at most `size` (size <= 0 is treated as one chunk). */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size <= 0) return items.length ? [items.slice()] : [];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export interface RunSendAllOptions {
  /** Override the chunk size (tests). Defaults to SEND_ALL_CHUNK_SIZE. */
  chunkSize?: number;
  /** A stable base for the per-chunk clientRequestId (`${baseId}:${index}`). Inject crypto.randomUUID(). */
  baseId: string;
  /** Progress callback after each chunk: (recordsProcessed, totalRecords). Monotonic, ends at total. */
  onProgress?: (processed: number, total: number) => void;
}

/**
 * Send every id in `ids` by chunking and POSTing each chunk sequentially. `post` receives the chunk's
 * recordIds and the per-chunk clientRequestId; it must perform the actual /api/intake/send call and
 * return the { ok, status, data } envelope. A failed chunk is recorded in `errors` and the loop
 * continues - so one transient failure never blocks the remaining records (a re-press resumes any
 * that stayed unsent). Returns the aggregate outcome.
 */
export async function runSendAll(
  ids: string[],
  post: (recordIds: string[], clientRequestId: string) => Promise<SendChunkResponse>,
  opts: RunSendAllOptions,
): Promise<SendAllOutcome> {
  const size = opts.chunkSize ?? SEND_ALL_CHUNK_SIZE;
  const chunks = chunk(ids, size);
  const outcome: SendAllOutcome = { chunks: chunks.length, sent: 0, skipped: 0, emailed: 0, errors: [], hasMore: false };

  let processed = 0;
  for (let i = 0; i < chunks.length; i++) {
    const part = chunks[i]!;
    try {
      const res = await post(part, `${opts.baseId}:${i}`);
      if (!res.ok) {
        outcome.errors.push(res.data?.error || `Send failed (${res.status})`);
      } else {
        outcome.sent += res.data?.counts?.sent ?? 0;
        outcome.skipped += res.data?.counts?.skipped ?? 0;
        outcome.emailed += (res.data?.emailedNoConsent ?? 0) + (res.data?.emailedUnableToTest ?? 0);
        // A 200 chunk can still carry hasMore: the server deferred its tail on its own deadline. Those
        // records are NOT sent (they hide in counts.skipped), so surface it for a re-press prompt.
        if (res.data?.hasMore) outcome.hasMore = true;
      }
    } catch (err) {
      outcome.errors.push(err instanceof Error ? err.message : String(err));
    }
    // Report records processed so far (cumulative ids in the chunks attempted), out of the total.
    processed += part.length;
    opts.onProgress?.(processed, ids.length);
  }

  return outcome;
}
